import CustomButton from "components/Atoms/CustomButton"
import StaticSelector from "components/Atoms/StaticSelector"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FormController from "components/Molecules/FormController"
import UserSelector from "components/Templates/selectors/UserSelector"
import { VENDORS } from "graphql/order/order.queries.gql"
import { GROUPS } from "graphql/selector.queries.gql"
import useErrors from "hooks/useErrors"
import { cloneDeep, set, update } from "lodash-es"
import orderModel from "models/order.model"
import React, { useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { ErrorToast } from "utils/AppToaster"
import { ORDER_TYPES } from "data/orderTypes"
import DeliveryType from "./DeliveryType"
import DisplayAddress from "./DisplayAddress"
import OrderFooter from "./OrderFooter"
import OrderHeader from "./OrderHeader"
import OrderItem from "./OrderItem"
import PickupLocations from "./PickupLocations"
import useOrderIds from "./useOrderIds"

const OrderForm = ({ order, onChange, distributionCenter }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const orders = useOrderIds()

  function handleChange(path, value) {
    if (order?.id) {
      return ErrorToast("Cannot edit the order, Please create a new one")
    }
    const orderCopy = cloneDeep(order)
    if (path === "group") {
      set(orderCopy, "user", null)
    }
    if (path === "dc") {
      set(orderCopy, "pickupSlots", [{}])
      // Basically remove all the order items where the sellable item stock has dc other than the one which is selected by the user
      // And show atleast 1 item
      update(orderCopy, "orderItems", oi => {
        const filtered = oi.filter(i => {
          const dc = i?.sellableItemStock?.distributionCentre
          return dc ? dc?.id === value?.id : true
        })
        return filtered?.length === 0 ? [{ quantity: 1 }] : filtered
      })
    }
    set(orderCopy, path, value)

    onChange(orderCopy)
  }
  const distributionCentres = [
    orders?.length > 0 ? order?.targetDcId?.id : distributionCenter?.id,
  ]

  const parsedVariables = useMemo(() => {
    return {
      userId: order.user?.id,
      orderItems: order.orderItems?.map(o => ({
        id: o?.id,
        sellableItemStockId: o?.sellableItemStock?.id,
        quantity: o?.quantity,
      })),
      pickupSlots:
        order.shippingMethod === "SPK"
          ? order?.pickupSlots?.map(p => ({
              distributionCentreId: order?.dc?.id,
              timeSlotId: p?.timeSlot?.id,
            }))
          : [],
      shippingMethod: order.shippingMethod,
    }
  }, [order])

  function handleAdd(path, value) {
    const orderCopy = cloneDeep(order)
    update(orderCopy, path, v => (v ? v?.concat(value) : [value]))
    onChange(orderCopy)
  }

  function handlRemove(path, index) {
    const orderCopy = cloneDeep(order)
    update(orderCopy, path, v => v?.filter((_, idx) => idx !== index))
    onChange(orderCopy)
  }
  const [errors] = useErrors({ schema: orderModel, state: parsedVariables })

  return (
    <>
      <FormController
        source={order}
        onChange={handleChange}
        onAdd={handleAdd as any}
        errors={errors?.details}
        onRemove={handlRemove as any}
      >
        <h6 className="mt-6">
          Ordering under - <strong>{distributionCenter?.name}</strong>{" "}
        </h6>
        <hr className="mt-0" />
        <div id="od_selectors" className="grid grid-cols-12 gap-4">
          <div className="col-span-3" data-testid="group-dropdown">
            <DynamicSelector
              label="Group"
              path="group"
              query={GROUPS}
              variables={{ category: "RTL" }}
            />
          </div>
          <div className="col-span-3" data-testid="user-dropdown">
            <UserSelector
              query={VENDORS}
              label="User"
              path="user"
              group={order?.group?.name}
              filter={{
                distributionCenterId: distributionCenter?.id,
              }}
            />
          </div>
          <div className="col-span-3" data-testid="order-category-dropdown">
            <StaticSelector
              label="Order category"
              path="orderCategory"
              labelElement="id"
              items={ORDER_TYPES.filter(order =>
                ["T0", "T2", "T7", "T8"].includes(order.id)
              )}
            />
          </div>
        </div>
        <OrderHeader />
        <hr className="mt-1" />
        <div id="od_items">
          {order.orderItems?.map((o, idx) => {
            const path = `orderItems.${idx}`
            return (
              <OrderItem
                key={o?.id ?? `item-${idx}`}
                distributionCentres={distributionCentres}
                path={path}
                index={idx}
                data={o}
              />
            )
          })}
        </div>
        <hr />
        <OrderFooter />
        <DeliveryType />
        <DisplayAddress />
        {order?.shippingMethod === "SPK" && (
          <PickupLocations distributionCenter={distributionCenter} />
        )}
        <div className="flex justify-end">
          <CustomButton
            intent="primary"
            icon="clipboard"
            text="View Summary"
            data-testid="view-summary-button"
            onClick={() => {
              const validOrderItems = order?.orderItems?.every(i => {
                const stock = i?.sellableItemStock
                const qty = +i?.quantity
                const relatedOfferStock = stock?.relatedStock?.offerStockCount
                if (!qty) {
                  return false
                }
                if (orders?.length > 0 && !stock?.currentStockCount) {
                  return false
                }
                return orders?.length > 0
                  ? qty <= stock?.currentStockCount
                  : relatedOfferStock
                  ? qty <= relatedOfferStock
                  : qty <= stock?.offerStockCount
              })

              if (!order?.user) {
                return ErrorToast("Please select a valid user for order")
              }
              // If all order items are valid then proceed towards the summary else show the user all the invalid errors
              if (validOrderItems) {
                navigate({
                  pathname: "invoice",
                  search: location.search,
                })
              } else {
                return order?.orderItems?.forEach(i => {
                  const stock = i?.sellableItemStock
                  const relatedOfferStock = stock?.relatedStock?.offerStockCount
                  // Cooerce quantity to a number before validating it
                  if (!+i.quantity) {
                    return ErrorToast(
                      `The specified quantity of ${stock?.sellableItem?.name} cannot be 0`
                    )
                  }

                  if (orders?.length > 0 && !stock?.currentStockCount) {
                    ErrorToast(
                      `Ground stock for ${stock?.sellableItem?.name} is not available `
                    )
                  }

                  if (stock) {
                    // If there is related stock on the stock item, then validate against it else validate it against offerStockCount
                    if (relatedOfferStock && i.quantity > relatedOfferStock) {
                      ErrorToast(
                        `The specified quantity of ${stock?.sellableItem?.name} is greater than the related stock qty (${relatedOfferStock})`
                      )
                    } else if (i.quantity > stock?.offerStockCount) {
                      ErrorToast(
                        `The specified quantity of ${stock?.sellableItem?.name} is greater than the available stock (${stock?.offerStockCount})`
                      )
                    }
                  }
                })
              }
            }}
          />
        </div>
      </FormController>
    </>
  )
}

export default OrderForm
